import { Alert, Button, Card, Descriptions, Form, Input, Skeleton, Space, Tag, Typography } from 'antd'
import { useState } from 'react'
import { useIssuePersonalTokenMutation, usePersonalTokenPrecheckQuery } from './tokensQueries'
import { getIssueErrorMessage, getPrecheckWarnings, PersonalTokenPrecheckStatuses } from './tokensModel'

const { Paragraph, Text } = Typography

type FormValues = {
  email: string
}

const TokensSection = () => {
  const [form] = Form.useForm<FormValues>()
  const [submittedEmail, setSubmittedEmail] = useState('')
  const precheckQuery = usePersonalTokenPrecheckQuery(submittedEmail)
  const mutation = useIssuePersonalTokenMutation()

  const result = precheckQuery.data
  const isOk = result?.status === PersonalTokenPrecheckStatuses.Ok
  const warnings = result && isOk ? getPrecheckWarnings(result) : []

  const handleSubmit = ({ email }: FormValues) => {
    mutation.reset()
    setSubmittedEmail(email.trim())
  }

  return (
    <Card title="Issue Personal Token" style={{ maxWidth: 760 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert showIcon type="info" description={<span>
          Personal Tokens are used to access the MCP server.
          They do not expire, and could technically be used to access
          anything in the web or mobile APIs as well as that user.
          So take care when issuing them.
        </span>} />

        <Paragraph style={{ marginBottom: 0 }}>
          Look up a user by email to check their plan and roles, then issue a personal access token on
          their behalf.
        </Paragraph>

        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginBottom: 0 }}>
          <Form.Item
            label="User email"
            name="email"
            rules={[{ required: true, whitespace: true, message: 'Enter a user email' }]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="user@example.com" allowClear autoComplete="off" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={precheckQuery.isFetching}>
              Find User
            </Button>
          </Form.Item>
        </Form>

        {precheckQuery.isLoading && submittedEmail !== '' && <Skeleton active />}

        {precheckQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to run the precheck"
            description="Check the email and try again."
          />
        )}

        {result?.status === PersonalTokenPrecheckStatuses.UserNotFound && (
          <Alert
            type="warning"
            showIcon
            message="No user found"
            description={`No user was found for ${submittedEmail}.`}
          />
        )}

        {result?.user && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="User">
              <Text strong>{result.user.displayName}</Text> ({result.user.email})
            </Descriptions.Item>
            <Descriptions.Item label="User ID">
              <Text code>{result.user.userId}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Active">
              {result.user.isActive ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}
            </Descriptions.Item>
            {result.tenant && (
              <Descriptions.Item label="Tenant">
                <Text strong>{result.tenant.name}</Text> (ID {result.tenant.id})
              </Descriptions.Item>
            )}
            {isOk && (
              <Descriptions.Item label="Plan">{result.planName || '—'}</Descriptions.Item>
            )}
            {isOk && (
              <Descriptions.Item label="Roles">
                {result.roles.length === 0 ? (
                  '—'
                ) : (
                  <Space size={[4, 4]} wrap>
                    {result.roles.map(role => (
                      <Tag key={role}>{role}</Tag>
                    ))}
                  </Space>
                )}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {result?.status === PersonalTokenPrecheckStatuses.TenantNotFound && (
          <Alert
            type="warning"
            showIcon
            message="No tenant found"
            description="The user was found but is not associated with a tenant, so a token cannot be issued."
          />
        )}

        {warnings.map(warning => (
          <Alert key={warning} type="warning" showIcon message={warning} />
        ))}

        {isOk && (
          <Button
            type="primary"
            onClick={() => mutation.mutate(submittedEmail)}
            loading={mutation.isLoading}
          >
            Issue Token
          </Button>
        )}

        {mutation.data?.wasIssued && (
          <Alert
            type="success"
            showIcon
            message="Token issued"
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>Copy this token now — it will not be shown again.</Text>
                <Paragraph
                  copyable={{ text: mutation.data.token ?? '' }}
                  code
                  style={{ marginBottom: 0, wordBreak: 'break-all' }}
                >
                  {mutation.data.token}
                </Paragraph>
              </Space>
            }
          />
        )}

        {mutation.data && !mutation.data.wasIssued && (
          <Alert
            type="error"
            showIcon
            message="Token not issued"
            description={mutation.data.error ?? 'The token could not be issued.'}
          />
        )}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Token not issued"
            description={getIssueErrorMessage(mutation.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default TokensSection
